import React, { ReactNode, useEffect, useState } from 'react';

import { CarouselReducerAction } from '../../CarouselReducer';
import {
  CarouselControlArrows,
  slideDirection,
} from '../../CarouselInterfaces';

import { BackgroundImage } from '../../../../atoms/BackgroundImage';
import {
  useBemBlockClassNames,
  useBemModifierClassNames,
} from '../../hooks/useBemClassNames';

interface DirectionalArrowProps {
  /**
   * Arrow is hidden
   **/
  isHidden?: boolean;
  /**
   * Arrows settings
   */
  settings: Required<CarouselControlArrows>;
  /**
   * Carousel global event dispatcher
   **/
  carouselDispatch: (action: CarouselReducerAction) => void;
  /**
   * ReactNode for Arrow display
   */
  child?: ReactNode;
}

interface ArrowProps extends DirectionalArrowProps {
  /**
   * Component global class
   */
  componentClass?: string;
  /**
   * Arrow direction
   */
  direction: slideDirection;
  /**
   * text for screen reader
   */
  srOnlyText: string;
  /**
   * ReactNode for Arrow display
   */
  children: ReactNode;
}

export const PreviousArrow = ({
  settings,
  carouselDispatch,
  isHidden = false,
  child = null,
}: DirectionalArrowProps) => (
  <Arrow
    settings={settings}
    direction="prev"
    srOnlyText="Previous slides"
    isHidden={isHidden}
    carouselDispatch={carouselDispatch}
  >
    {child || (
      <BackgroundImage
        src={'https://images.playosmo.com/osmo-carousel-arrow.svg'}
        style={{ backgroundSize: 'contain' }}
        className="icon"
      />
    )}
  </Arrow>
);

export const NextArrow = ({
  settings,
  carouselDispatch,
  isHidden = false,
  child = null,
}: DirectionalArrowProps) => (
  <Arrow
    settings={settings}
    direction="next"
    srOnlyText="Next slides"
    isHidden={isHidden}
    carouselDispatch={carouselDispatch}
  >
    {child || (
      <BackgroundImage
        src={'https://images.playosmo.com/osmo-carousel-arrow.svg'}
        style={{ backgroundSize: 'contain' }}
        className="icon"
      />
    )}
  </Arrow>
);

const Arrow = ({
  componentClass = 'arrow',
  settings,
  direction,
  srOnlyText,
  children,
  isHidden,
  carouselDispatch,
}: ArrowProps) => {
  const [mainClasses, setMainClasses] = useState<string[]>([]);

  useEffect(() => {
    const newMainClasses = [
      settings.items.classes?.component
        ? settings.items.classes.component
        : componentClass,
    ];
    if (settings.items.classes?.custom) {
      newMainClasses.push(settings.items.classes.custom);
    }
    setMainClasses(newMainClasses);
  }, [settings, componentClass]);

  const arrowClasses = useBemBlockClassNames(mainClasses);
  const arrowClassesDirectionModifier = useBemModifierClassNames(
    mainClasses,
    `${direction}-direction`,
  );
  const arrowClassesIsHiddenModifier = useBemModifierClassNames(
    mainClasses,
    'is-hidden',
  );

  return (
    <button
      className={`${arrowClasses} ${
        direction ? arrowClassesDirectionModifier : ''
      } ${isHidden ? arrowClassesIsHiddenModifier : ''}`}
      onClick={() =>
        carouselDispatch({ type: direction, payload: { source: 'arrow' } })
      }
    >
      <div aria-hidden="true">{children}</div>
      <span className="sr-only">{srOnlyText}</span>
    </button>
  );
};

export default Arrow;
